import { useEffect, useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";
import { api } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { CASE_INTERNAL_KEYS, HELPER_GATED_KEYS } from "../hooks/sectionMeta";
import { useTranslation } from "../i18n";
import { roleLabel } from "../hooks/useRoleLabels";
import { LockButton, AddButton } from "./SharedUI";
import { choosableCo } from "../lib/collaboratorState";
import { RAD, FS, Z } from "../lib/tokens";
import { paneLabel, NOT_IN_VIEW_KEY } from "../lib/sectionPanes.mjs";
import Table from "./Table";

// Phase M+2b Chunk B+C — RP per-case "Team access" panel.
//
// Visible to admin OR RP-of-case (RP-of-cc for ccId). Cells render the resolved
// firm default for the target user's role and switch to a coloured Grant /
// Restrict pill when an override exists. Two-state cycle toggles the override
// on/off:
//   firm default = Yes → Restrict → Yes
//   firm default = No  → Grant    → No
//
// Phase 37 (pieces 2 + 3):
//  - Columns are grouped by the app's CV/CCV SUBSECTIONS (from the section
//    catalog) into individually-collapsible blocks, collapsed by default — the
//    old single 64-wide horizontal matrix is gone.
//  - For a non-admin RP, columns are bounded by the firm switch-off list (only
//    delegable rights are grantable). Admin sees every case-internal right.
//  - A per-grant EXPIRY control lets an RP set a temporary elevation; the backend
//    already honours expiresAt at every read locus.

// Display-label fallbacks for when the catalog hasn't loaded yet.
//
// Brief 36 §3.5 asked whether these English literals should go through t().
// They already do, and they STAY as they are. keyLabel() below is
// `t("section." + k, { fallback: … || SECTION_LABELS[k] || k })` — this map is
// the third term of that call's fallback chain, not a rendered string, and a
// `fallback:` argument is SUPPOSED to be the English literal at the call site.
// Measured, so it is not an argument from principle: all ten of these keys have
// complete en/fr/nl `section.*` rows today, so no supported language can reach
// this map at all — it needs a missing seed row AND an unloaded catalog.
//
// What DOES render English here is a different thing and belongs to brief 34:
// 43 of the 87 case-internal keys have no `section.<key>` row, so keyLabel()
// falls to `m.displayLabel || m.label` — the server's hardcoded English, out of
// sectionRegistry.js. That is a computed key (`section.` + a runtime value),
// which is why i18n-keys-complete.js counts it and cannot check it.
const SECTION_LABELS: Record<string, string> = {
  "caseView.terms": "Case Info — Terms",
  "caseView.financials": "Case Info — Financials & summary",
  "caseView.parties": "Parties",
  "caseView.tasks.view": "View tasks",
  "caseView.tasks.edit": "Edit tasks",
  "caseView.tasks.delete": "Delete tasks",
  "caseView.timeEntries.view": "View time entries",
  "caseView.documents.view": "View documents",
  "caseView.agenda.view": "View agenda",
  "caseView.chat": "Case chat",
};

// Brief 36 §3.6 — the `label:` fields these two carried were dead: the pill text
// comes from t("teamAccess.grant"/"teamAccess.restrict") at the render site, and
// nothing has read PILL_STYLE[...].label since. Deleted rather than translated.
const PILL_STYLE: Record<string, any> = {
  grant: {
    bg: "rgba(34,197,94,.18)",
    color: "var(--gn)",
    border: "1px solid rgba(34,197,94,.35)",
  },
  restrict: {
    bg: "rgba(245,158,11,.18)",
    color: "var(--am)",
    border: "1px solid rgba(245,158,11,.35)",
  },
};
const DEFAULT_FALLBACK_STYLE = {
  bg: "transparent",
  color: "var(--tm)",
  border: "1px dashed var(--br)",
  label: "Default",
};

// Phase 28 (E) — view/edit pane split.
const isViewKey = (k: any) => k.endsWith(".view");

// Phase 37 S3 correction 1 — the viewing + editing panes are merged into ONE
// matrix per subsection. A subsection's CRUD rights sit side by side as
// columns in view | create | edit | delete order. These short action labels
// are used as column headers for genuine quad keys (a base carrying ≥2 of the
// four CRUD bits); standalone / operation keys keep their full label so they
// stay unambiguous (e.g. caseView.successFee.edit, collectiveCase.bulkMailer.view).
const QUAD_ACTIONS: Record<string, string> = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

export default function TeamAccessPanel({
  scope,
  scopeId,
  allCollaborators,
  paneFilter,
  showExtras = true,
  title,
  locked = false,
  onToggleLock,
}: any) {
  const { t } = useTranslation();
  const { viewer, can, refetch, swapping } = usePermissions();
  const [grants, setGrants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<any>(null);
  const [busy, setBusy] = useState<Set<any>>(new Set());
  const [extraTeammates, setExtraTeammates] = useState<any[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [rpIds, setRpIds] = useState<any[]>([]);
  const [roleDefaults, setRoleDefaults] = useState<any>(null);
  const [finGrants, setFinGrants] = useState<any[]>([]);
  const [finBusy, setFinBusy] = useState<Set<any>>(new Set());
  // Phase 37 — section catalog (subsection grouping) + switch-off list + collapse
  // + per-grant expiry editor.
  const [catalog, setCatalog] = useState<any>(null);
  const [delegable, setDelegable] = useState<any>(null); // { key: bool } or null=admin/unbounded
  const [openSubs, setOpenSubs] = useState<Record<string, any>>({}); // collapsed by default
  const [expiryOpen, setExpiryOpen] = useState<any>(null); // `${id}:${key}` whose editor is open

  const isCC = scope === "cc";
  const queryParam = isCC ? `ccId=${scopeId}` : `caseId=${scopeId}`;

  const isAdmin = viewer?.isAdmin === true;
  const myRp = isCC ? viewer?.myRpCcIds || [] : viewer?.myRpCaseIds || [];
  const visible = isAdmin || (Array.isArray(myRp) && myRp.includes(scopeId));

  // Columns: case-internal keys the granter holds (admin: all). Phase 37 — for a
  // non-admin RP, additionally bounded by the firm switch-off list.
  const columns = useMemo(() => {
    const all = Array.from(CASE_INTERNAL_KEYS).filter((k) =>
      isCC
        ? k.startsWith("collectiveCase.") || k.startsWith("caseView.")
        : k.startsWith("caseView."),
    );
    let base;
    if (isCC) {
      const ccKeys = all.filter((k) => k.startsWith("collectiveCase."));
      // Phase 41 (D-gate-3) — a non-admin here is an RP-of-scope (panel is
      // RP-visible), so helper-gated rights they hold via RP must show even
      // though clientCan() can't see that authority. Allowlist filter still
      // applies below; server holdsRight() is the authoritative guard.
      base = isAdmin
        ? ccKeys
        : ccKeys.filter(
            (k) => can(k, { ccId: scopeId }) || HELPER_GATED_KEYS.has(k),
          );
    } else if (isAdmin) {
      base = all.filter((k) => k.startsWith("caseView."));
    } else {
      base = all.filter(
        (k) =>
          k.startsWith("caseView.") &&
          (can(k, { caseId: scopeId }) || HELPER_GATED_KEYS.has(k)),
      );
    }
    // Phase 37 — non-admin RPs may only hand out delegable rights.
    if (!isAdmin && delegable) base = base.filter((k) => delegable[k] !== false);
    if (paneFilter === "view") return base.filter(isViewKey);
    if (paneFilter === "edit") return base.filter((k) => !isViewKey(k));
    return base;
  }, [isCC, isAdmin, scopeId, can, paneFilter, delegable]);

  // Group the columns by subsection (catalog order). Each group is collapsible.
  const groupedColumns = useMemo(() => {
    const set = new Set(columns);
    if (!catalog) {
      // Fallback: one synthetic group preserving order.
      return columns.length
        ? [{ subId: "_all", subLabel: "", subLabelKey: "", keys: columns }]
        : [];
    }
    const bySub: Record<string, any> = {};
    for (const m of catalog.sections) {
      if (!set.has(m.key)) continue;
      (bySub[m.subsection || "_misc"] ||= []).push(m.key);
    }
    const out: any[] = [];
    for (const ss of catalog.subsections) {
      const keys = bySub[ss.id];
      if (keys && keys.length)
        out.push({ subId: ss.id, subLabel: ss.label, subLabelKey: ss.labelKey, keys });
    }
    if (bySub["_misc"])
      out.push({ subId: "_misc", subLabel: "Other", subLabelKey: "permSubsection._misc", keys: bySub["_misc"] });
    return out;
  }, [columns, catalog]);

  useEffect(() => {
    if (!visible || swapping) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/access-grants?${queryParam}`)
      .then((rows) => {
        if (cancelled) return;
        setGrants(Array.isArray(rows) ? rows : []);
      })
      .catch((e) => {
        if (!cancelled)
          setErr(
            e?.message ||
              t("teamAccess.failed_to_load_grants", { fallback: "Failed to load grants" }),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, queryParam, swapping]);

  useEffect(() => {
    if (!visible || swapping) return;
    let cancelled = false;
    api
      .get(`/financial-grants?${queryParam}`)
      .then((rows) => {
        if (!cancelled) setFinGrants(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible, queryParam, swapping]);

  useEffect(() => {
    if (!visible || swapping) return;
    let cancelled = false;
    api
      .get("/role-defaults")
      .then((map) => {
        if (cancelled) return;
        setRoleDefaults(map && typeof map === "object" ? map : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible, swapping]);

  // Phase 37 — section catalog (subsection grouping) + switch-off list.
  useEffect(() => {
    if (!visible || swapping) return;
    let cancelled = false;
    (
      api.get("/permissions/catalog") as Promise<{
        sections?: unknown[];
      } | null>
    )
      .then((c) => {
        if (!cancelled && c && Array.isArray(c.sections)) setCatalog(c);
      })
      .catch(() => {});
    (
      api.get("/firm-settings/case-team-allowlist") as Promise<{
        delegable?: Record<string, boolean>;
      } | null>
    )
      .then((a) => {
        if (!cancelled && a && a.delegable) setDelegable(a.delegable);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible, swapping]);

  useEffect(() => {
    if (!visible || swapping) return;
    let cancelled = false;
    const url = isCC
      ? `/collective-cases/${scopeId}/responsible-partners`
      : `/cases/${scopeId}/responsible-partners`;
    api
      .get(url)
      .then((rps) => {
        if (cancelled) return;
        const ids = Array.isArray(rps) ? rps.map((r) => r.collaboratorId) : [];
        setRpIds(ids);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible, isCC, scopeId, swapping]);

  const rowIds = useMemo(() => {
    const set = new Set();
    rpIds.forEach((id) => set.add(id));
    grants.forEach((g) => set.add(g.collaboratorId));
    extraTeammates.forEach((id) => set.add(id));
    return Array.from(set);
  }, [rpIds, grants, extraTeammates]);

  if (!visible) return null;

  function getRoleName(collaboratorId: any) {
    const c = (allCollaborators || []).find((x: any) => x.id === collaboratorId);
    return c?.role || null;
  }

  function getFirmDefault(collaboratorId: any, sectionKey: any) {
    if (!roleDefaults) return false;
    const role = getRoleName(collaboratorId);
    if (!role) return false;
    return roleDefaults[role]?.[sectionKey] === true;
  }

  function getGrant(collaboratorId: any, sectionKey: any) {
    return (
      grants.find(
        (x) => x.collaboratorId === collaboratorId && x.sectionKey === sectionKey,
      ) || null
    );
  }

  function getCellState(collaboratorId: any, sectionKey: any) {
    const g = getGrant(collaboratorId, sectionKey);
    if (g) return { state: g.allowed ? "grant" : "restrict", grantId: g.id, expiresAt: g.expiresAt };
    if (!roleDefaults) return { state: "default-text", grantId: null };
    const firmDefault = getFirmDefault(collaboratorId, sectionKey);
    return { state: firmDefault ? "default-yes" : "default-no", grantId: null };
  }

  // POST a grant (allowed + optional expiry), then refetch. Shared by the cell
  // cycle and the expiry editor.
  async function postGrant(collaboratorId: any, sectionKey: any, allowed: any, expiresAt: any) {
    const body = isCC
      ? { ccId: scopeId, collaboratorId, sectionKey, allowed, expiresAt: expiresAt || null }
      : { caseId: scopeId, collaboratorId, sectionKey, allowed, expiresAt: expiresAt || null };
    await api.post("/access-grants", body);
    const fresh = await api.get(`/access-grants?${queryParam}`);
    setGrants(Array.isArray(fresh) ? fresh : []);
    refetch();
  }

  async function handleCellClick(collaboratorId: any, sectionKey: any) {
    if (locked) return;
    const cellKey = `${collaboratorId}:${sectionKey}`;
    if (busy.has(cellKey)) return;
    const existingGrant = getGrant(collaboratorId, sectionKey);
    const grantId = existingGrant?.id || null;
    const firmDefault = getFirmDefault(collaboratorId, sectionKey);

    let action: any; // 'delete' | { allowed }
    if (existingGrant) action = "delete";
    else if (firmDefault) action = { allowed: false };
    else action = { allowed: true };

    setBusy((prev) => new Set(prev).add(cellKey));
    setErr(null);
    try {
      if (action === "delete") {
        if (grantId) await api.del(`/access-grants/${grantId}?type=${isCC ? "cc" : "case"}`);
        const fresh = await api.get(`/access-grants?${queryParam}`);
        setGrants(Array.isArray(fresh) ? fresh : []);
        refetch();
      } else {
        await postGrant(collaboratorId, sectionKey, action.allowed, null);
      }
    } catch (e: any) {
      setErr(e?.message || t("common.save_failed", { fallback: "Save failed" }));
    } finally {
      setBusy((prev) => {
        const n = new Set(prev);
        n.delete(cellKey);
        return n;
      });
    }
  }

  async function setExpiry(collaboratorId: any, sectionKey: any, isoDateOrNull: any) {
    if (locked) return;
    const cellKey = `${collaboratorId}:${sectionKey}`;
    setBusy((prev) => new Set(prev).add(cellKey));
    setErr(null);
    try {
      // expiry only meaningful on an allow-grant; keep allowed:true.
      const expiresAt = isoDateOrNull ? new Date(isoDateOrNull + "T23:59:59").toISOString() : null;
      await postGrant(collaboratorId, sectionKey, true, expiresAt);
      setExpiryOpen(null);
    } catch (e: any) {
      setErr(e?.message || t("common.save_failed", { fallback: "Save failed" }));
    } finally {
      setBusy((prev) => {
        const n = new Set(prev);
        n.delete(cellKey);
        return n;
      });
    }
  }

  function describeCollab(id: any) {
    const c = (allCollaborators || []).find((x: any) => x.id === id);
    if (!c) return `#${id}`;
    return [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || `#${id}`;
  }
  // Brief 36 §1 — Role.name is an identifier ("ExecutiveSecretary"), not a
  // label. roleLabel() resolves it through Role.displayLabel{En,Fr,Nl}, which
  // is what the assigned-collaborators pane of this same "Case Team" section
  // already does (CaseView.tsx:4462, :4609). Unresolved roles fall through to
  // the name, i.e. exactly what this rendered before.
  function describeCollabRole(id: any) {
    const c = (allCollaborators || []).find((x: any) => x.id === id);
    return roleLabel(c?.role || "");
  }

  function finGrantFor(collaboratorId: any) {
    return finGrants.find((g) => g.collaboratorId === collaboratorId) || null;
  }
  async function toggleFinGrant(collaboratorId: any) {
    if (locked) return;
    const key = `fin:${collaboratorId}`;
    if (finBusy.has(key)) return;
    setFinBusy((p) => new Set(p).add(key));
    const existing = finGrantFor(collaboratorId);
    try {
      if (existing) await api.del(`/financial-grants/${existing.id}?type=${isCC ? "cc" : "case"}`);
      else
        await api.post(
          "/financial-grants",
          isCC ? { ccId: scopeId, collaboratorId } : { caseId: scopeId, collaboratorId },
        );
      const fresh = await api.get(`/financial-grants?${queryParam}`);
      setFinGrants(Array.isArray(fresh) ? fresh : []);
      refetch();
    } catch (e: any) {
      setErr(e?.message || t("common.save_failed", { fallback: "Save failed" }));
    } finally {
      setFinBusy((p) => {
        const n = new Set(p);
        n.delete(key);
        return n;
      });
    }
  }

  const subtitle = t("teamAccess.subtitle_rp_exceptions", {
    fallback: "Responsible partner: make exceptions to default settings here.",
  });

  const pickerCandidates = choosableCo(allCollaborators)
    .filter((c: any) => {
      if (rowIds.includes(c.id)) return false;
      const q = pickerQ.trim().toLowerCase();
      if (!q) return true;
      const name = [c.firstName, c.lastName].filter(Boolean).join(" ").toLowerCase();
      return name.includes(q) || (c.email || "").toLowerCase().includes(q);
    })
    .slice(0, 8);

  const wrapStyle = {
    marginTop: 8,
    padding: 12,
    borderRadius: RAD.r8,
    border: "1px solid var(--br)",
    background: "var(--bi)",
    overflow: "visible",
  };
  const keyLabel = (k: any) => {
    const m = catalog?.sections?.find((s: any) => s.key === k);
    return t(`section.${k}`, { fallback: (m && (m.displayLabel || m.label)) || SECTION_LABELS[k] || k });
  };

  return (
    <div className="detail-card" style={wrapStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 6,
          position: "relative",
        }}
      >
        {title && (
          <h4
            style={{
              fontSize: FS.s11,
              fontWeight: 700,
              color: "var(--ts)",
              textTransform: "uppercase",
              letterSpacing: ".4px",
              margin: 0,
            }}
          >
            {title}
          </h4>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
          <AddButton
            /* 188 §F.3 [glance correction] — the standard label (186 (7));
               `teamAccess.add_teammate` ("+ Ajouter un collaborateur") stays
               seeded, unread. */
            label={t("common.add_button.add", { fallback: "Add" })}
            onClick={() => setPickerOpen((o) => !o)}
            visible={!locked}
            {...({} as any)}
          />
          {onToggleLock && <LockButton locked={locked} onToggle={onToggleLock} {...({} as any)} />}
        </div>
        {pickerOpen && !locked && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: 4,
              padding: 8,
              background: "var(--bc)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              width: 260,
              zIndex: Z.panelPopover,
              boxShadow: "0 6px 20px rgba(0,0,0,.5)",
            }}
          >
            <input
              type="text"
              autoFocus
              placeholder={t("teamAccess.search_collaborators", { fallback: "Search collaborators…" })}
              value={pickerQ}
              onChange={(e) => setPickerQ(e.target.value)}
              style={{
                width: "100%",
                padding: "4px 6px",
                background: "var(--bi)",
                border: "1px solid var(--br)",
                borderRadius: RAD.r4,
                color: "var(--tp)",
                fontSize: FS.s11,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            <div style={{ marginTop: 6, maxHeight: 180, overflowY: "auto" }}>
              {pickerCandidates.length === 0 && (
                <p style={{ fontSize: FS.s10, color: "var(--tm)", margin: "4px 2px" }}>
                  {t("common.no_matches", { fallback: "No matches." })}
                </p>
              )}
              {pickerCandidates.map((c: any) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setExtraTeammates((prev) => (prev.includes(c.id) ? prev : [...prev, c.id]));
                    setPickerOpen(false);
                    setPickerQ("");
                  }}
                  style={{ padding: "4px 6px", cursor: "pointer", borderRadius: RAD.r3, fontSize: FS.s11 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bh)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ fontWeight: 500 }}>
                    {[c.firstName, c.lastName].filter(Boolean).join(" ") || c.email}
                  </div>
                  <div style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                    {roleLabel(c.role || "")} · {c.email}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showExtras && (
        <p style={{ margin: "0 0 4px", fontSize: FS.s10_5, color: "var(--tm)" }}>{subtitle}</p>
      )}

      <div>
        {loading && (
          <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 8 }}>
            {t("common.loading", { fallback: "Loading…" })}
          </p>
        )}
        {err && <p style={{ fontSize: FS.s11, color: "var(--rd)", marginTop: 8 }}>{err}</p>}
        {!loading && rowIds.length === 0 && (
          <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 8 }}>
            {t("teamAccess.no_teammates2", {
              fallback: "No teammates on this case yet. Use “Add teammate” to include one.",
            })}
          </p>
        )}
        {!loading && rowIds.length > 0 && columns.length === 0 && (
          <p style={{ fontSize: FS.s11, color: "var(--tm)", marginTop: 8 }}>
            {t("teamAccess.no_grantable", {
              fallback: "You don't hold any grantable permissions on this case.",
            })}
          </p>
        )}
        {/* Phase 37 — one collapsible block per subsection (collapsed by default),
            each a compact teammates × keys grid for that subsection's rights. */}
        {!loading &&
          rowIds.length > 0 &&
          groupedColumns.map((g) => {
            const open = !!openSubs[g.subId];
            // Short CRUD header only for a genuine quad key — a base that
            // carries ≥2 of the four bits in this subsection. Op keys that
            // happen to end in .view/.edit (successFee.edit, bulkMailer.view)
            // have no quad sibling, so they fall back to their full label.
            const quadByBase: Record<string, string[]> = {};
            for (const k of g.keys) {
              const i = k.lastIndexOf(".");
              const suf = k.slice(i + 1);
              if (QUAD_ACTIONS[suf]) (quadByBase[k.slice(0, i)] ||= []).push(suf);
            }
            const isQuad = (k: string) => {
              const i = k.lastIndexOf(".");
              return (
                !!QUAD_ACTIONS[k.slice(i + 1)] &&
                (quadByBase[k.slice(0, i)]?.length || 0) > 1
              );
            };
            const colLabel = (k: string) => {
              const suf = k.slice(k.lastIndexOf(".") + 1);
              if (isQuad(k)) return t(`teamAccess.col_${suf}`, { fallback: QUAD_ACTIONS[suf] });
              return keyLabel(k);
            };

            // ── WHAT THE ACTION WORD GOVERNS, UNDER THE ACTION WORD ───────────
            //
            // A quad column said "View" and nothing else; what it governed was
            // in a `title` that also printed the raw section key
            // (`caseView.timeEntries.view`) — 82i's class, developer output
            // reaching a lawyer. The key is gone from the tooltip and the
            // description is on screen instead.
            //
            // THE VISIBLE LINE IS THE DISTINGUISHING MIDDLE, NOT THE WHOLE
            // LABEL. `keyLabel` resolves a PATH — "CC — Client list — view",
            // "Case Info — Terms — view" — whose head repeats the subsection
            // heading a few pixels above and whose tail repeats the action word
            // beside it. What is left is the part that discriminates, so the
            // last segment before the action is what is drawn. Splitting on the
            // em dash holds in every language because the catalog and the
            // `section.*` seed rows share the convention: "Agenda — view",
            // "Agenda — consulter", "Agenda — bekijken".
            //
            // AND IT IS DRAWN ONLY WHERE THE ACTION WORD IS ACTUALLY AMBIGUOUS
            // — where this block holds more than one column group. Eight of the
            // fifteen subsections are a single quad and nothing else ("Tasks",
            // "Parties", "Agenda", …): there the four columns ARE the table and
            // the heading above already names them, so a second line would only
            // repeat it four times, and "Timeline & procedural stages" repeated
            // under four columns is worse than nothing. The seven that carry
            // two quads or a quad beside standalone rights are the ones a
            // person cannot read: "Time & billing" shows View twice (time
            // entries and the time log), and "Terms & rates" puts "Edit" next
            // to "Edit success fee".
            const groupCount =
              Object.values(quadByBase).filter((b) => b.length > 1).length +
              g.keys.filter((k: string) => !isQuad(k)).length;
            const colScope = (k: string) => {
              if (!isQuad(k) || groupCount < 2) return "";
              const parts = keyLabel(k).split(/\s+[—–]\s+/);
              return (parts.length > 1 ? parts[parts.length - 2] : parts[0]).trim();
            };
            return (
              <div
                key={g.subId}
                // The subsection id, in the markup. Inert — no CSS reads it and
                // no behaviour depends on it — and it is here for two readers:
                // `scripts/snapshot-dom.js`'s EXPAND list, which opens exactly
                // one of these blocks by name (83w) and would otherwise have to
                // guess at a heading's text; and anybody reading the recording,
                // where these blocks are otherwise anonymous divs. Same shape as
                // `data-secollapsed` on the case sections one level up.
                data-subsection={g.subId}
                style={{ border: "1px solid var(--br)", borderRadius: RAD.r8, overflow: "hidden", marginTop: 8 }}
              >
                <div
                  onClick={() => setOpenSubs((p) => ({ ...p, [g.subId]: !p[g.subId] }))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    background: "rgba(74,124,255,.10)",
                    cursor: "pointer",
                  }}
                >
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ fontSize: FS.s11, fontWeight: 600, color: "var(--tp)" }}>
                    {g.subLabelKey ? t(g.subLabelKey, { fallback: g.subLabel }) : g.subLabel || t("teamAccess.rights", { fallback: "Rights" })}
                  </span>
                  <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>({g.keys.length})</span>
                  {/* 83x — WHICH SCREEN THESE RIGHTS GOVERN, on the heading and
                      never in a tooltip. "Identity & description" never said
                      which pane was at stake; "Identity & description · Case
                      Info" does. The name is not typed here — `SUBSECTION_PANE`
                      points at the i18n key the pane itself renders, so a
                      vertical that renames its matters renames this too, once,
                      through the same TranslationOverride row. */}
                  {(() => {
                    const p = paneLabel(g.subId, (k: string) => t(k, { fallback: k }));
                    if (!p) return null;
                    return (
                      <span style={{ fontSize: FS.s9_5, color: "var(--tm)", marginLeft: 2 }}>
                        · {p.text}
                        {!p.here && (
                          <span style={{ fontStyle: "italic" }}>
                            {" "}
                            {t(NOT_IN_VIEW_KEY, { fallback: "(not in this view)" })}
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                {open && (
                  <div style={{ overflowX: "auto" }}>
                    <Table style={{ width: "100%", borderCollapse: "collapse", fontSize: FS.s11 }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              padding: "4px 6px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "var(--tm)",
                              borderBottom: "1px solid var(--br)",
                              whiteSpace: "nowrap",
                              position: "sticky",
                              left: 0,
                              background: "var(--bi)",
                              zIndex: Z.stickyCell,
                            }}
                          >
                            {t("teamAccess.teammate", { fallback: "Teammate" })}
                          </th>
                          {g.keys.map((k: any) => {
                            const scope = colScope(k);
                            return (
                              <th
                                key={k}
                                style={{
                                  padding: "4px 8px",
                                  textAlign: "center",
                                  fontWeight: 600,
                                  color: "var(--tm)",
                                  borderBottom: "1px solid var(--br)",
                                  whiteSpace: "nowrap",
                                }}
                                // NO TOOLTIP. It used to carry the raw section
                                // key, then the full label — and the full label
                                // is the two visible lines read back in the
                                // other order, so it said nothing and said it
                                // only on hover. What a reader actually wanted
                                // was WHICH SCREEN, and that is on the heading
                                // bar above, always visible (83x).
                              >
                                {colLabel(k)}
                                {scope && (
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      fontSize: FS.s9,
                                      color: "var(--tm)",
                                      opacity: 0.85,
                                      marginTop: 1,
                                    }}
                                  >
                                    {scope}
                                  </div>
                                )}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {rowIds.map((id: any) => (
                          <tr key={id}>
                            <td
                              style={{
                                padding: "4px 6px",
                                borderBottom: "1px solid rgba(255,255,255,.04)",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                left: 0,
                                background: "var(--bi)",
                                zIndex: Z.stickyHead,
                              }}
                            >
                              <div style={{ fontWeight: 500 }}>{describeCollab(id)}</div>
                              <div style={{ fontSize: FS.s9, color: "var(--tm)" }}>{describeCollabRole(id)}</div>
                            </td>
                            {g.keys.map((k: any) => (
                              <td
                                key={k}
                                style={{
                                  padding: "4px 8px",
                                  textAlign: "center",
                                  borderBottom: "1px solid rgba(255,255,255,.04)",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "top",
                                }}
                              >
                                <Cell
                                  id={id}
                                  k={k}
                                  state={getCellState(id, k)}
                                  busy={busy.has(`${id}:${k}`)}
                                  locked={locked}
                                  onClick={() => handleCellClick(id, k)}
                                  expiryOpen={expiryOpen === `${id}:${k}`}
                                  onToggleExpiry={() =>
                                    setExpiryOpen((cur: any) => (cur === `${id}:${k}` ? null : `${id}:${k}`))
                                  }
                                  onSetExpiry={(d: any) => setExpiry(id, k, d)}
                                  t={t}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

// One grant cell: the default/grant/restrict pill plus, for an active Grant, a
// compact expiry affordance (Phase 37).
function Cell({ id, k, state, busy, locked, onClick, expiryOpen, onToggleExpiry, onSetExpiry, t }: any) {
  const isDefault =
    state.state === "default-yes" || state.state === "default-no" || state.state === "default-text";
  const baseBtn = {
    fontSize: FS.s9_5,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: RAD.r10,
    cursor: locked ? "default" : busy ? "wait" : "pointer",
    opacity: busy ? 0.6 : 1,
    fontFamily: "inherit",
  };
  let pill;
  if (state.state === "default-yes" || state.state === "default-no") {
    pill = (
      <button
        type="button"
        disabled={busy || locked}
        onClick={onClick}
        style={{ ...baseBtn, background: "transparent", color: "var(--tm)", border: "1px solid transparent" }}
      >
        {state.state === "default-yes" ? "✓" : "✗"}
      </button>
    );
  } else if (state.state === "default-text") {
    pill = (
      <button
        type="button"
        disabled={busy || locked}
        onClick={onClick}
        style={{ ...baseBtn, background: DEFAULT_FALLBACK_STYLE.bg, color: DEFAULT_FALLBACK_STYLE.color, border: DEFAULT_FALLBACK_STYLE.border }}
      >
        {t("teamAccess.default", { fallback: DEFAULT_FALLBACK_STYLE.label })}
      </button>
    );
  } else {
    const ps = PILL_STYLE[state.state];
    pill = (
      <button
        type="button"
        disabled={busy || locked}
        onClick={onClick}
        style={{ ...baseBtn, background: ps.bg, color: ps.color, border: ps.border }}
      >
        {state.state === "grant"
          ? t("teamAccess.grant", { fallback: "Grant" })
          : t("teamAccess.restrict", { fallback: "Restrict" })}
      </button>
    );
  }

  // Expiry only on an active allow-grant.
  const showExpiry = state.state === "grant";
  const expDate = state.expiresAt ? String(state.expiresAt).slice(0, 10) : "";
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
      {pill}
      {showExpiry && !locked && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          <button
            type="button"
            onClick={onToggleExpiry}
            title={t("teamAccess.set_expiry", { fallback: "Set expiry (temporary elevation)" })}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: expDate ? "var(--am)" : "var(--tm)",
              fontSize: FS.s9,
              fontFamily: "inherit",
            }}
          >
            <Clock size={9} />
            {expDate ? expDate : t("teamAccess.expiry_add", { fallback: "expiry" })}
          </button>
          {expiryOpen && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              <input
                type="date"
                defaultValue={expDate}
                onChange={(e) => e.target.value && onSetExpiry(e.target.value)}
                style={{
                  fontSize: FS.s9,
                  padding: "1px 2px",
                  background: "var(--bi)",
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r3,
                  color: "var(--tp)",
                  fontFamily: "inherit",
                }}
              />
              {expDate && (
                <button
                  type="button"
                  onClick={() => onSetExpiry(null)}
                  title={t("teamAccess.expiry_clear", { fallback: "Clear expiry (make permanent)" })}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--tm)", fontSize: FS.s10, padding: 0 }}
                >
                  ✕
                </button>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
